import { ReactElement } from 'react';

import PacksList from '@/components/app/cards/PacksList';
import GuideList from '@/components/app/program/GuideList';
import Narrator from '@/components/app/program/Narrator';
import ProgramFaveHeart from '@/components/app/program/ProgramFaveHeart';
import ProgramPlayButton from '@/components/app/program/ProgramPlayButton';
import UpsellBanner from '@/components/UpsellBanner';
import { usePacksFeed } from '@/hooks/app/usePacksFeed';
import { useUpsellBannerContent } from '@/hooks/app/useUpsellBannerContent/useUpsellBannerContent';

import {
	ContentColumn,
	Description,
	FaveHeartContainer,
	GuideListWrapper,
	NarratorWrapper,
	PlayButtonWrapper,
	Title,
	TitleRow,
	Wrapper,
} from './styles';
import { ProgramLayoutProps } from './types';

const ProgramLayout = ({ program }: ProgramLayoutProps): ReactElement => {
	const packFeedName = program ? `program-${program.id}` : null;
	const { packs, isLoading } = usePacksFeed(packFeedName);
	const { upsellCopy } = useUpsellBannerContent();

	// `isLoading` (SWR's isValidating) is true only while the request is in flight and
	// settles to false once it resolves OR rejects — for any reason, including a
	// network-level failure with no HTTP status. So holding on `isLoading` alone avoids
	// flashing the legacy GuideList while the feed is still in flight, without ever
	// getting stuck: once the request settles, whether via a confirmed empty feed, a 404,
	// or an unretried network error (usePacksFeed sets errorRetryCount: 0), packs.length
	// stays 0 and we fall back to GuideList rather than looping back into "still loading".
	const showPacksList = isLoading || packs.length > 0;

	return (
		<Wrapper data-testid="program-layout">
			{upsellCopy && <UpsellBanner />}
			<ContentColumn>
				{program?.title && (
					<TitleRow>
						<Title>{program.title}</Title>
						{program.id && (
							<FaveHeartContainer>
								<ProgramFaveHeart programId={program.id} programTitle={program.title} />
							</FaveHeartContainer>
						)}
					</TitleRow>
				)}
				<NarratorWrapper>
					<Narrator program={program} />
				</NarratorWrapper>
				{showPacksList ? (
					<PacksList
						packs={packs}
						isLoading={isLoading}
						packFeedName={packFeedName ?? undefined}
						hideUpsellBanner
					/>
				) : (
					program?.guides && (
						<>
							<PlayButtonWrapper>
								<ProgramPlayButton program={program} />
							</PlayButtonWrapper>
							{program.description && <Description>{program.description}</Description>}
							<GuideListWrapper>
								<GuideList program={program} />
							</GuideListWrapper>
						</>
					)
				)}
			</ContentColumn>
		</Wrapper>
	);
};

export default ProgramLayout;
